import type { ReactNode } from 'react';
import { StatusDot } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { ClosedAlert, HealthAlerts, OpenAlert } from './health-types';
import { openSeconds, severityTone, severityWord } from './health-words';

function OpenRow({ alert, now }: { alert: OpenAlert; now: number }): ReactNode {
  const f = useFormat();
  return (
    <li className={`hl-alert hl-alert--${alert.severity}`}>
      <StatusDot tone={severityTone[alert.severity]} label={severityWord[alert.severity]} />
      <div className="hl-alert__body">
        <p className="hl-alert__title" title={alert.key}>
          {alert.title}
        </p>
        <p className="hl-alert__when">
          <span title={f.dateTime(alert.openedAt)}>
            open {f.duration(openSeconds(alert.openedAt, now))}
          </span>
          <span>
            {alert.lastMailedAt ? `mailed ${f.ago(alert.lastMailedAt, now)}` : 'not mailed'}
          </span>
        </p>
      </div>
      <span className="hl-alert__sev">{severityWord[alert.severity]}</span>
    </li>
  );
}

function ClosedRow({ alert, now }: { alert: ClosedAlert; now: number }): ReactNode {
  const f = useFormat();
  return (
    <li className="hl-alert hl-alert--closed">
      <StatusDot tone="neutral" label={`${severityWord[alert.severity]}, closed`} />
      <div className="hl-alert__body">
        <p className="hl-alert__title" title={alert.key}>
          {alert.title}
        </p>
        <p className="hl-alert__when">
          <span>open {f.duration(openSeconds(alert.openedAt, alert.closedAt))}</span>
          <span title={f.dateTime(alert.closedAt)}>closed {f.ago(alert.closedAt, now)}</span>
        </p>
      </div>
      <span className="hl-alert__sev">{severityWord[alert.severity]}</span>
    </li>
  );
}

/**
 * Open alerts, critical first, each with how long it has been open and when a mail last named it; then the
 * alerts that closed in the last seven days, greyed, with how long each was open.
 */
export function AlertsPanel({ alerts, now }: { alerts: HealthAlerts; now: number }): ReactNode {
  return (
    <>
      {alerts.open.length === 0 ? (
        <p className="hl-clear">
          <StatusDot tone="success" label="Nothing open" /> Nothing open.
        </p>
      ) : (
        <ul className="hl-alerts" aria-label="Open alerts">
          {alerts.open.map((a) => (
            <OpenRow key={a.key} alert={a} now={now} />
          ))}
        </ul>
      )}
      <h3 className="hl-sub">Closed in the last 7 days</h3>
      {alerts.closed.length === 0 ? (
        <p className="pl-part__quiet">None closed.</p>
      ) : (
        <ul className="hl-alerts hl-alerts--closed" aria-label="Closed alerts">
          {alerts.closed.map((a) => (
            <ClosedRow key={`${a.key}-${a.closedAt}`} alert={a} now={now} />
          ))}
        </ul>
      )}
    </>
  );
}
